import React, { useState, useEffect, useCallback } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Alert,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { getPropertyGroups } from "../../property/api/actions";
import { parse, format, isValid, isBefore, startOfDay } from "date-fns";

const InputHidden = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
});

const DropZone = styled(Box)(({ theme, isDragActive }) => ({
  border: `2px dashed ${isDragActive ? theme.palette.primary.main : theme.palette.grey[300]}`,
  borderRadius: theme.shape.borderRadius,
  padding: theme.spacing(4),
  textAlign: 'center',
  cursor: 'pointer',
  transition: 'all 0.2s ease-in-out',
  backgroundColor: isDragActive ? theme.palette.primary.light + '10' : theme.palette.grey[50],
  '&:hover': {
    borderColor: theme.palette.primary.main,
    backgroundColor: theme.palette.primary.light + '05',
  },
  minHeight: '120px',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: theme.spacing(1),
}));

function CSVUpload(props) {
  const [file, setFile] = useState(null);
  const [csvData, setCsvData] = useState([]);
  const [validationErrors, setValidationErrors] = useState([]);
  const [isValidating, setIsValidating] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadResults, setUploadResults] = useState(null);
  const [isDragActive, setIsDragActive] = useState(false);
  const [pendingFile, setPendingFile] = useState(null);

  const propertyGroups = props.properties?.propertyGroups?.items || [];
  const groupsLoading = props.properties?.propertyGroups?.isLoading || false;
  const groupsError = props.properties?.propertyGroups?.error;

  useEffect(() => {
    if (props.open) {
      props.getPropertyGroups({ limit: 10000, page: 1 });
      resetForm();
    }
  }, [props.open]);

  useEffect(() => {
    if (!groupsLoading && csvData.length > 0 && propertyGroups.length > 0) {
      const errors = validateCSVData(csvData);
      setValidationErrors(errors);
    }
  }, [groupsLoading, propertyGroups.length, csvData.length]);

  useEffect(() => {
    if (!groupsLoading && pendingFile && propertyGroups.length > 0 && !groupsError) {
      const selectedFile = pendingFile;
      setPendingFile(null);
      setFile(selectedFile);
      setIsValidating(true);

      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const csvText = e.target.result;
          const parsedData = parseCSV(csvText);
          const errors = validateCSVData(parsedData);
          
          setCsvData(parsedData);
          setValidationErrors(errors);
          setIsValidating(false);
        } catch (error) {
          setValidationErrors([error.message]);
          setIsValidating(false);
        }
      };
      reader.readAsText(selectedFile);
    }
  }, [groupsLoading, propertyGroups.length, pendingFile, groupsError]);

  const resetForm = () => {
    setFile(null);
    setCsvData([]);
    setValidationErrors([]);
    setIsValidating(false);
    setIsUploading(false);
    setUploadResults(null);
    setIsDragActive(false);
    setPendingFile(null);
  };

  const parseCSV = (csvText) => {
    const lines = csvText.trim().split('\n');
    if (lines.length < 2) {
      throw new Error('CSV file must have at least a header row and one data row');
    }

    const headers = lines[0].split(',').map(h => h.trim().toLowerCase());
    const data = [];

    for (let i = 1; i < lines.length; i++) {
      const values = lines[i].split(',').map(v => v.trim());
      if (values.length !== headers.length) {
        throw new Error(`Row ${i + 1} has ${values.length} columns but expected ${headers.length}`);
      }

      const row = {};
      headers.forEach((header, index) => {
        row[header] = values[index];
      });
      data.push(row);
    }

    return data;
  };

  const validateCSVData = (data) => {
    const errors = [];
    const groupMap = new Map();
    
    if (propertyGroups.length === 0) {
      if (groupsLoading) {
        errors.push('Property groups are still loading. Please wait...');
        return errors;
      } else if (groupsError) {
        errors.push('Failed to load property groups. Please try again or contact support.');
        return errors;
      } else {
        errors.push('No property groups available. Please ensure groups exist before uploading.');
        return errors;
      }
    }
    
    propertyGroups.forEach(group => {
      groupMap.set(group.name.toLowerCase(), group.id);
    });

    data.forEach((row, index) => {
      const rowNum = index + 2;

      if (!row.groupname || !row.groupname.trim()) {
        errors.push(`Row ${rowNum}: Group name is required`);
      }

      if (!row.scheduleddate || !row.scheduleddate.trim()) {
        errors.push(`Row ${rowNum}: Scheduled date is required`);
      } else {
        const dateStr = row.scheduleddate.trim();
        let date = null;
        const formats = ['MM/dd/yyyy', 'yyyy-MM-dd', 'MM-dd-yyyy'];
        
        for (const fmt of formats) {
          const parsed = parse(dateStr, fmt, new Date());
          if (isValid(parsed)) {
            date = parsed;
            break;
          }
        }
        
        if (!date || !isValid(date)) {
          errors.push(`Row ${rowNum}: Invalid date format. Use MM/DD/YYYY, YYYY-MM-DD, or MM-DD-YYYY`);
        } else if (isBefore(startOfDay(date), startOfDay(new Date()))) {
          errors.push(`Row ${rowNum}: Scheduled date must be in the future`);
        }
      }

      if (row.groupname && !groupMap.has(row.groupname.toLowerCase())) {
        const availableGroups = propertyGroups.map(g => g.name).join(', ');
        const errorMsg = availableGroups 
          ? `Row ${rowNum}: Group "${row.groupname}" not found. Available groups: ${availableGroups}`
          : `Row ${rowNum}: Group "${row.groupname}" not found.`;
        errors.push(errorMsg);
      }

      if (row.isactive && !['true', 'false', '1', '0', 'yes', 'no'].includes(row.isactive.toLowerCase())) {
        errors.push(`Row ${rowNum}: isActive must be true/false, 1/0, or yes/no`);
      }
    });

    return errors;
  };

  const processFile = useCallback((selectedFile) => {
    if (!selectedFile) return;

    if (!selectedFile.name.toLowerCase().endsWith('.csv')) {
      setValidationErrors(['Please upload a CSV file.']);
      return;
    }

    if (groupsLoading) {
      setPendingFile(selectedFile);
      setFile(selectedFile);
      setValidationErrors(['Loading property groups. Validation will begin once groups are loaded...']);
      return;
    }

    if (groupsError) {
      setFile(selectedFile);
      setValidationErrors(['Failed to load property groups. Please close and reopen the dialog to retry.']);
      return;
    }

    setFile(selectedFile);
    setIsValidating(true);

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const csvText = e.target.result;
        const parsedData = parseCSV(csvText);
        const errors = validateCSVData(parsedData);
        
        setCsvData(parsedData);
        setValidationErrors(errors);
        setIsValidating(false);
      } catch (error) {
        setValidationErrors([error.message]);
        setIsValidating(false);
      }
    };
    reader.readAsText(selectedFile);
  }, [groupsLoading, groupsError, propertyGroups]);

  const handleFileChange = (event) => {
    console.log(event.target.files, 'files')
    const selectedFile = event.target.files[0];
    processFile(selectedFile);
  };

  const handleDragEnter = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(false);
  }, []);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(false);

    // Don't process if groups are still loading
    if (groupsLoading) {
      return;
    }

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      processFile(files[0]);
    }
  }, [groupsLoading, processFile]);

  const handleUpload = async () => {
    if (validationErrors.length > 0) {
      return;
    }

    setIsUploading(true);
    
    try {
      const propertyGroups = props.properties?.propertyGroups?.items || [];
      const groupMap = new Map();
      
      propertyGroups.forEach(group => {
        groupMap.set(group.name.toLowerCase(), group.id);
      });

      const schedules = csvData.map(row => {
        const dateStr = row.scheduleddate.trim();
        let date = null;
        const formats = ['MM/dd/yyyy', 'yyyy-MM-dd', 'MM-dd-yyyy'];
        
        for (const fmt of formats) {
          const parsed = parse(dateStr, fmt, new Date());
          if (isValid(parsed)) {
            date = parsed;
            break;
          }
        }
        
        const dateOnly = date ? format(date, 'yyyy-MM-dd') : dateStr.split(' ')[0];
        return {
          groupId: groupMap.get(row.groupname.toLowerCase()),
          scheduledDisconnectDate: dateOnly ? `${dateOnly} 00:00:00` : `${dateStr} 00:00:00`,
          notes: row.notes || '',
          isActive: row.isactive
            ? (['true', '1', 'yes'].includes(row.isactive.toLowerCase()) ? 1 : 0)
            : 1
        };
      });

      const result = await props.postBatch(schedules);
      setUploadResults({
        success: true,
        message: `Successfully uploaded ${schedules.length} disconnect schedules`,
        schedules: result
      });
      
      props.get();
    } catch (error) {
      setUploadResults({
        success: false,
        message: error.message || 'Upload failed',
        error: error
      });
    } finally {
      setIsUploading(false);
    }
  };

  const handleClose = () => {
    resetForm();
    props.onClose();
  };

  const getCSVTemplate = () => {
    const headers = ['groupname', 'scheduleddate', 'notes', 'isactive'];
    const sampleData = [
      ['Group test batch 1', '12/25/2025', 'Test Notes', 'true'],
      ['Group test batch 2', '01/15/2026', 'Test Notes', 'true']
    ];
    
    const csvContent = [
      headers.join(','),
      ...sampleData.map(row => row.join(','))
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'disconnect_schedules_template.csv';
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Dialog
      open={props.open} 
      onClose={handleClose}
      maxWidth="lg"
      fullWidth
    >
      <DialogTitle>
        <Typography variant="h6">
          Upload Disconnect Schedules
        </Typography>
      </DialogTitle>
      
      <DialogContent>
        {/* Show loading state for property groups */}
        {groupsLoading && (
          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="body2">
              Loading property groups... Please wait before uploading your CSV file.
            </Typography>
            <LinearProgress sx={{ mt: 1 }} />
          </Alert>
        )}

        {/* Show error state for property groups */}
        {groupsError && !groupsLoading && (
          <Alert severity="error" sx={{ mb: 2 }}>
            <Typography variant="body2">
              Failed to load property groups. Please close this dialog and try again.
            </Typography>
            {groupsError.response?.data?.message && (
              <Typography variant="caption" sx={{ mt: 1, display: 'block' }}>
                Error: {groupsError.response.data.message}
              </Typography>
            )}
          </Alert>
        )}

        <Box sx={{ mb: 3 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Upload a CSV file to create disconnect schedules batch.
          </Typography>
          
          <DropZone
            isDragActive={isDragActive && !groupsLoading}
            onDragEnter={groupsLoading ? undefined : handleDragEnter}
            onDragLeave={groupsLoading ? undefined : handleDragLeave}
            onDragOver={groupsLoading ? undefined : handleDragOver}
            onDrop={groupsLoading ? undefined : handleDrop}
            onClick={() => !groupsLoading && document.getElementById('csv-file-input').click()}
            sx={{
              opacity: groupsLoading ? 0.6 : 1,
              pointerEvents: groupsLoading ? 'none' : 'auto',
              cursor: groupsLoading ? 'not-allowed' : 'pointer',
            }}
          >
            {file ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <InsertDriveFileIcon sx={{ fontSize: 40, color: 'primary.main' }} />
                <Box sx={{ textAlign: 'left' }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    {file.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {(file.size / 1024).toFixed(1)} KB
                  </Typography>
                </Box>
              </Box>
            ) : (
              <Box>
                <CloudUploadIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
                <Typography variant="h6" sx={{ mb: 1, color: 'text.secondary' }}>
                  Drag and drop a CSV file here
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  or click to select a file
                </Typography>
              </Box>
            )}
            
            <InputHidden
              id="csv-file-input"
              type="file"
              accept=".csv"
              onChange={handleFileChange}
              disabled={groupsLoading}
            />
          </DropZone>
          
          <Box sx={{ mt: 2, textAlign: 'center' }}>
            <Button
              variant="text"
              startIcon={<FileDownloadIcon />}
              onClick={getCSVTemplate}
              size="small"
            >
              Download CSV Template
            </Button>
          </Box>
        </Box>

        {(isValidating || (groupsLoading && pendingFile)) && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ mb: 1 }}>
              {groupsLoading && pendingFile 
                ? 'Waiting for property groups to load before validating...' 
                : 'Validating CSV data...'}
            </Typography>
            <LinearProgress />
          </Box>
        )}

        {validationErrors.length > 0 && (
          <Alert severity="error" sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>Validation Errors:</Typography>
            {validationErrors.map((error, index) => (
              <Typography key={index} variant="body2">• {error}</Typography>
            ))}
          </Alert>
        )}

        {csvData.length > 0 && validationErrors.length === 0 && (
          <Box sx={{ mb: 2 }}>
            <Alert severity="success" sx={{ mb: 2 }}>
              <Typography variant="body2">
                CSV file is valid! Found {csvData.length} schedules to upload.
              </Typography>
            </Alert>
            
            <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>Preview:</Typography>
            <TableContainer 
              component={Paper} 
              sx={{ 
                maxHeight: 300,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1
              }}
            >
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>Group Name</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Scheduled Date</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Notes</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Active</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {csvData.slice(0, 10).map((row, index) => (
                    <TableRow key={index} hover>
                      <TableCell>{row.groupname}</TableCell>
                      <TableCell>{row.scheduleddate}</TableCell>
                      <TableCell>{row.notes || '-'}</TableCell>
                      <TableCell>
                        <Chip 
                          label={row.isactive || 'true'} 
                          color={row.isactive === 'false' || row.isactive === '0' ? 'error' : 'success'}
                          size="small"
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            {csvData.length > 10 && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                Showing first 10 rows of {csvData.length} total rows
              </Typography>
            )}
          </Box>
        )}

        {uploadResults && (
          <Alert 
            severity={uploadResults.success ? "success" : "error"}
            sx={{ mb: 2 }}
          >
            {uploadResults.success ? (
              <CheckCircleIcon sx={{ mr: 1 }} />
            ) : (
              <ErrorIcon sx={{ mr: 1 }} />
            )}
            {uploadResults.message}
          </Alert>
        )}

        {isUploading && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ mb: 1 }}>Uploading schedules...</Typography>
            <LinearProgress />
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} variant="outlined">
          {uploadResults?.success ? 'Close' : 'Cancel'}
        </Button>
        <Button 
          onClick={handleUpload}
          variant="contained"
          disabled={csvData.length === 0 || validationErrors.length > 0 || isUploading || groupsLoading || groupsError}
          startIcon={!isUploading && <CloudUploadIcon />}
        >
          {isUploading ? 'Uploading...' : 'Upload Schedules'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

CSVUpload.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  postBatch: PropTypes.func.isRequired,
  get: PropTypes.func.isRequired,
  getPropertyGroups: PropTypes.func.isRequired,
  properties: PropTypes.object.isRequired,
};

function State(state) {
  return {
    properties: state.properties,
  };
}

export default connect(State, { getPropertyGroups })(CSVUpload);
